import React, { useCallback, useState } from 'react'

import { Alert, AlertIcon, Box, Spinner } from '@chakra-ui/react'

interface ZoomableImageIProps {
  url: string
  name: string
  zoomLevel: number
  modalSize: '3xl' | 'full'
  isDragging: boolean
  containerRef: React.RefObject<HTMLDivElement>
  onMouseDown: (e: React.MouseEvent<HTMLDivElement>) => void
  onMouseMove: (e: React.MouseEvent<HTMLDivElement>) => void
  onMouseUp: (e: React.MouseEvent<HTMLDivElement>) => void
  onMouseLeave: (e: React.MouseEvent<HTMLDivElement>) => void
  onToggleModalSize: () => void
}

const LAYOUT_CONSTANTS = {
  VIEWPORT_OFFSET: '100px',
  NORMAL_MAX_HEIGHT: '800px',
  SCROLLBAR_SIZE: '8px',
  ZOOM_PERCENTAGE_MULTIPLIER: 100,
  EMBED_HEIGHT: 800,
} as const

const getFullHeightStyle = () =>
  `calc(100vh - ${LAYOUT_CONSTANTS.VIEWPORT_OFFSET})`

const getCursorStyle = (
  zoomLevel: number,
  isDragging: boolean,
  modalSize: '3xl' | 'full'
) => {
  if (zoomLevel > 1) {
    return isDragging ? 'grabbing' : 'grab'
  }
  return modalSize === '3xl' ? 'pointer' : 'default'
}

const getScrollbarStyles = (modalSize: '3xl' | 'full') =>
  ({
    maxHeight:
      modalSize === 'full'
        ? getFullHeightStyle()
        : LAYOUT_CONSTANTS.NORMAL_MAX_HEIGHT,
    maxWidth: '100%',
    '&::-webkit-scrollbar': {
      width: LAYOUT_CONSTANTS.SCROLLBAR_SIZE,
      height: LAYOUT_CONSTANTS.SCROLLBAR_SIZE,
    },
    '&::-webkit-scrollbar-track': {
      bg: 'gray.100',
      borderRadius: 'md',
    },
    '&::-webkit-scrollbar-thumb': {
      bg: 'gray.400',
      borderRadius: 'md',
      '&:hover': {
        bg: 'gray.500',
      },
    },
  } as any)

const getImageStyles = (zoomLevel: number, modalSize: '3xl' | 'full') => {
  const baseStyles = {
    display: 'block',
    userSelect: 'none' as const,
    WebkitUserSelect: 'none' as const,
    pointerEvents: 'none' as const,
    transition: 'width 0.2s ease-out, transform 0.2s ease-out',
    height: 'auto',
  }

  if (zoomLevel === 1) {
    return {
      ...baseStyles,
      objectFit: 'contain' as const,
      maxWidth: '100%',
      maxHeight:
        modalSize === 'full'
          ? getFullHeightStyle()
          : LAYOUT_CONSTANTS.NORMAL_MAX_HEIGHT,
      width: 'auto',
    }
  }

  return {
    ...baseStyles,
    maxWidth: 'none',
    maxHeight: 'none',
    width: `${zoomLevel * LAYOUT_CONSTANTS.ZOOM_PERCENTAGE_MULTIPLIER}%`,
  }
}

export const ZoomableImage = ({
  url,
  name,
  zoomLevel,
  modalSize,
  isDragging,
  containerRef,
  onMouseDown,
  onMouseMove,
  onMouseUp,
  onMouseLeave,
  onToggleModalSize,
}: ZoomableImageIProps) => {
  const [isImageLoading, setIsImageLoading] = useState(true)
  const [isImageError, setIsImageError] = useState(false)

  const handleImageLoad = useCallback(() => {
    setIsImageLoading(false)
  }, [])

  const handleImageError = useCallback(() => {
    setIsImageError(true)
    setIsImageLoading(false)
  }, [])

  const handleClick = useCallback(() => {
    if (!isDragging && modalSize === '3xl') {
      onToggleModalSize()
    }
  }, [isDragging, modalSize, onToggleModalSize])

  if (isImageError) {
    return (
      <Alert status='error'>
        <AlertIcon />
        Failed to load image
      </Alert>
    )
  }

  return (
    <Box
      cursor={getCursorStyle(zoomLevel, isDragging, modalSize)}
      onClick={handleClick}
      onMouseDown={onMouseDown}
      onMouseLeave={onMouseLeave}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      overflow={zoomLevel > 1 ? 'auto' : 'hidden'}
      position='relative'
      ref={containerRef}
      sx={getScrollbarStyles(modalSize)}
    >
      {isImageLoading && (
        <Spinner
          left='50%'
          position='absolute'
          top='50%'
          transform='translate(-50%, -50%)'
        />
      )}
      <img
        alt={name}
        onError={handleImageError}
        onLoad={handleImageLoad}
        src={url}
        style={getImageStyles(zoomLevel, modalSize)}
      />
    </Box>
  )
}
